<script setup lang="ts">
import { computed, type ComputedRef } from 'vue'
import { storeToRefs } from 'pinia'
import PopupMenu from '@/components/PopupMenu.vue'
import type { MenuItemType } from '@/components/dropdown-menu/types'
import { useLibraryStore } from '@/library/library.store'
import { LinkType, getLinkType, normalizeUrl, ATTACHMENT_TYPE, OPEN_METHOD } from '@/library/utils/urlUtils'
import { getLinkIconSrc } from '@/library/utils/linkIcons'
import { getAttachmentLinkDisplayText } from '@/library/utils/linkDisplayText'
import { timeAgo } from '@/util/time'
import FolderAvatar from '@/library/FolderAvatar.vue'
import OpensInNewTabIcon from '@/components/OpensInNewTabIcon.vue'
import ConfirmDialog from 'primevue/confirmdialog'
import { useConfirm } from 'primevue/useconfirm'
import GbButton from '@/components/GbButton.vue'
import { useLibraryFolderAnalytics } from '@/library/composables/useLibraryFolderAnalytics'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'

const props = defineProps<{
  attachment: any
}>()

const emit = defineEmits<{
  edit: [attachment: any]
  deleted: []
  error: [payload: { message: string; error: unknown }]
}>()

const confirm = useConfirm()

const libraryStore = useLibraryStore()
const {
  canDeleteCurrentFolderContent,
  doesUserOwnCurrentFolder,
  currentFolder,
  getContributorIdsForContent,
} = storeToRefs(libraryStore)

const { getReactiveEventAttributes } = useLibraryFolderAnalytics()
const analytics = getAnalyticsTracker().getChildContext(getReactiveEventAttributes())

const canManage = computed(() => (canDeleteCurrentFolderContent.value || doesUserOwnCurrentFolder.value) && props.attachment?.content_type !== 'attachment_file')

const linkType = computed(() => getLinkType(props.attachment?.content_body?.url || ''))

const linkIconSrc = computed((): string => {
  return getLinkIconSrc(linkType.value)
})

const displayTitle = computed(() => {
  return props.attachment?.content_title?.trim() || props.attachment?.content_body?.url || ''
})

const linkHref = computed((): string | undefined => {
  return normalizeUrl(props.attachment?.content_body?.url)
})

const linkAriaLabel = computed((): string => {
  const title = displayTitle.value
  if (!title) return 'Open link (opens in a new tab)'
  return `${title} (opens in a new tab)`
})

const menuTriggerAriaLabel = computed((): string => {
  const title = displayTitle.value
  if (!title) return 'Attachment options'
  return `Attachment options for ${title}`
})

const timeText = computed((): string => {
  return props.attachment?.updated_at ? timeAgo(props.attachment.updated_at) : ''
})

const getLinkDisplayText = computed((): string => {
  const url = props.attachment?.content_body?.url || ''
  return getAttachmentLinkDisplayText({
    url,
    linkType: linkType.value,
    timeText: timeText.value
  })
})

const contentId = computed((): string => {
  return props.attachment?.content_uuid || ''
})

type ContributorInfo = { contributor: string; isFolderOwner: boolean }

const contributorInfo: ComputedRef<ContributorInfo | undefined> = computed(() => {
  return getContributorIdsForContent.value?.[contentId.value]
})

const contributorUUID: ComputedRef<string> = computed(() => {
  return contributorInfo.value?.contributor || ''
})

const shouldShowContributorByline: ComputedRef<boolean> = computed(() => {
  return !!(contributorUUID.value && !contributorInfo.value?.isFolderOwner)
})

const deleteDialogGroup = computed(() => `linkcard-delete-${props.attachment?.content_uuid || 'unknown'}`)

const handleCardClick = () => {
  analytics.sendEvent(EVENT_NAMES.LIBRARY.ATTACHMENT.OPENED_LIBRARY_ATTACHMENT, {
    link_type: linkType.value,
    attachment_type: ATTACHMENT_TYPE.LINK,
    attachment_uuid: contentId.value,
    open_method: OPEN_METHOD.CARD_CLICK
  })
}

const menuItems = computed((): MenuItemType[] => {
  const items: MenuItemType[] = []

  if (props.attachment?.content_body?.url) {
    items.push({
      label: 'Open',
      href: linkHref.value,
      target: '_blank',
      icon: OpensInNewTabIcon,
      iconProps: { size: 'sm' },
      action: () => {
        analytics.sendEvent(EVENT_NAMES.LIBRARY.ATTACHMENT.OPENED_LIBRARY_ATTACHMENT, {
          link_type: linkType.value,
          attachment_type: ATTACHMENT_TYPE.LINK,
          attachment_uuid: contentId.value,
          open_method: OPEN_METHOD.MENU_OPEN
        })
      }
    })
  }

  items.push({
    label: 'Edit',
    disabled: !canManage.value,
    action: () => {
      analytics.sendEvent(EVENT_NAMES.LIBRARY.ATTACHMENT.CLICKED_EDIT_ATTACHMENT, {
        attachment_type: ATTACHMENT_TYPE.LINK,
        attachment_uuid: contentId.value
      })
      emit('edit', props.attachment)
    }
  })

  items.push({
    label: 'Delete',
    disabled: !canManage.value,
    action: () => {
      confirm.require({
        group: deleteDialogGroup.value,
        header: 'Delete Link',
        message: 'Are you sure you want to delete this link?',
        accept: async () => {
          try {
            const contentUuid = props.attachment?.content_uuid
            if (!contentUuid) {
              throw new Error('Missing attachment content_uuid')
            }

            const collectionUuid = currentFolder.value?.uuid || ''
            if (!collectionUuid) {
              throw new Error('Missing folder uuid for deletion')
            }

            analytics.sendEvent(EVENT_NAMES.LIBRARY.ATTACHMENT.CLICKED_DELETE_ATTACHMENT)

            await libraryStore.deleteResource({
              uuid: contentUuid,
              type: 'attachment_link',
              collection: collectionUuid
            })

            emit('deleted')
          } catch (e) {
            console.error('Failed to delete attachment link', e)
            const details = e instanceof Error ? e.message : String(e)
            emit('error', { message: `Failed to delete attachment: ${details}`, error: e })
          }
        },
      })
    }
  })
  return items
})
</script>

<template>
  <a
    class="relative flex w-link-card cursor-pointer flex-col overflow-hidden rounded-xs bg-gray-50 px-2 py-3 shadow-lib-card hover:shadow-lib-card-hover transition-shadow"
    :href="linkHref"
    target="_blank"
    rel="noopener noreferrer"
    :aria-label="linkAriaLabel"
    @click="handleCardClick"
  >
    <div class="flex items-start justify-between">
      <div class="flex min-w-0 items-start gap-1.5">
        <div class="h-6 w-6 shrink-0">
          <img
            :src="linkIconSrc"
            alt=""
            class="block h-6 w-6 max-w-none"
            aria-hidden="true"
          />
        </div>

        <div class="min-w-0 flex-1">
          <div class="min-w-0">
            <div
              class="truncate text-md font-medium leading-5 text-toolkit-blue-hover-link"
            >
              {{ displayTitle }}
            </div>
            <div
              v-if="getLinkDisplayText"
              class="-mt-0.5 truncate text-sm leading-link-card-subtitle text-gray-700"
            >
              {{ getLinkDisplayText }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="menuItems.length" data-linkcard-menu class="shrink-0" @click.prevent.stop>
        <PopupMenu
          :items="menuItems"
          :triggerAriaLabel="menuTriggerAriaLabel"
          triggerClass="flex items-center justify-center h-6 w-6 rounded-full text-gray-900 hover:bg-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-700 focus-visible:bg-gray-300"
          triggerIconClass="text-text-2xl"
        />
      </div>
    </div>

    <div
      v-if="shouldShowContributorByline && contributorUUID"
      class="flex items-center gap-1 pl-link-card-indent text-sm leading-link-card-subtitle text-gray-700"
    >
      <span>Contributed by</span>
      <FolderAvatar
        :userId="contributorUUID"
        :folder="currentFolder"
        avatarSize="xs"
      />
    </div>

    <ConfirmDialog :group="deleteDialogGroup" class="w-auto md:w-[38rem]">
      <template #container="{ message, acceptCallback, rejectCallback }">
        <div class="flex flex-col rounded-md bg-surface-50">
          <div class="mt-2 flex justify-between border-b border-gray-300 p-3">
            <span class="block pl-3 text-xl font-medium">{{ message.header }}</span>
            <GbButton
              size="small"
              rounded
              :iconOnly="true"
              aria-label="close dialog"
              gbTheme="grayTertiary"
              class="mr-4 self-start"
              @click="rejectCallback"
            >
              <span class="material-icons-round">close</span>
            </GbButton>
          </div>
          <p class="py-5 pl-7 pr-12">{{ message.message }}</p>
          <div class="flex items-center justify-end gap-2 border-t border-gray-300 p-4">
            <GbButton label="Cancel" outlined @click="rejectCallback" gbTheme="grayOutlineSecondary"></GbButton>
            <GbButton label="Delete" @click="acceptCallback" gbTheme="redPrimary"></GbButton>
          </div>
        </div>
      </template>
    </ConfirmDialog>
  </a>
</template>
